import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, input, output, signal, computed, effect, inject, model } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { UiService } from '../../services/ui.service';
import { I18nService } from '../../services/i18n.service';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';
import { App_Routes } from '../../enums/router.enum';
import { LP_UI_CONFIG } from '../../core/tokens';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { Notification } from '../../models/classes/object';
import { AssetsService } from '../../services/asset.service';

@Component({
  selector: 'app-tab-menu',
  imports: [NzTabsModule, RouterModule, NzIconModule, NzBadgeModule],
  templateUrl: './tab-menu.html',
  styleUrls: ['./tab-menu.scss'],
  animations: [
    trigger('activeTab', [
      state('0', style({ left: '0%' })),
      state('1', style({ left: '33%' })),
      state('2', style({ left: '66%' })),
      state('3', style({ left: '150%' })),
      transition('* <=> *', [animate('0.25s cubic-bezier(0.35, 0, 0.25, 1)')]),
    ]),
  ],
})
export class TabMenuComponent {
  // ✅ Inputs using Angular 20 `input()`
  header = input(false);
  navigation = input(true);
  currentTab = model(0);

  // ✅ Outputs using Angular 20 `output()`
  tabChanged = output<number>();

  // ✅ Inject services
  public readonly cfg = inject(LP_UI_CONFIG);
  private router = inject(Router);
  private ui = inject(UiService);
  private assetsService = inject(AssetsService);
  public i18n = inject(I18nService);

  // ✅ Signals for state
  activeTab = signal<number>(this.ui.TabIndex() ?? GLOBAL_CONSTANT.DEFAULT_INITIALIZER);
  loading = signal<boolean>(false);
  notificationCount = signal<number>(GLOBAL_CONSTANT.DEFAULT_INITIALIZER);
  notifications = signal<Notification[]>([]);

  // ✅ Computed property
  isMobile = computed(() => this.ui.IsMobile());

  readonly numEquipmentNotifications = computed(
    () => this.notifications().filter((n) => !n.isEdgeDevice).length
  );

  readonly numEdgeDeviceNotifications = computed(
    () => this.notifications().filter((n) => n.isEdgeDevice).length
  );

  constructor() {
    // ✅ Effect for tab index changes
    effect(() => {
      const idx = this.ui.TabIndex;
      if (this.isMobile() && idx() === 2) {
        this.activeTab.set(-1);
      } else {
        this.activeTab.set(idx() ?? GLOBAL_CONSTANT.DEFAULT_INITIALIZER);
      }
    });

    // ✅ Effect for orientation changes
    effect(() => {
      const currentTab = this.activeTab();
      if (this.isMobile() && currentTab === 2) {
        this.activeTab.set(-1);
      } else if (!this.isMobile() && currentTab === -1) {
        this.activeTab.set(2);
      }
    });

    // ✅ Load notifications count
    this.refresh();
  }

  setTab(tabIndex: number) {
    this.currentTab.set(tabIndex);
  }
  private refresh() {
    this.loading.set(true);
    if (this.cfg.appName === 'em') {
      this.assetsService.subscribeToNotifications(
        (result: { notifications: Notification[]; filtered: boolean }) => {
          setTimeout(() => {
            this.notifications.set(
              result.notifications.filter((item) => item.notificationStatus !== 'repaired')
            );
            this.notificationCount.set(result.notifications.length);
          });
        }
      );
    }
  }

  onTabClicked(index: number) {
    this.activeTab.set(index);
    this.tabChanged.emit(index);
    setTimeout(() => this.navToTab(), 250);
  }

  private navToTab() {
    switch (this.activeTab()) {
      case 0:
        this.router.navigate([App_Routes.HOME]);
        break;
      case 1:
        this.router.navigate([App_Routes.NOTIFICATIONS]);
        break;
      case 2:
        this.router.navigate([App_Routes.SETTINGS]);
        break;
    }
  }
}
